import { endOfDay, startOfDay, sub } from 'date-fns';
import { create, StoreApi, UseBoundStore } from 'zustand';

import { SortingState } from '@tanstack/react-table';

import { serializeSort } from './helpers';

export { serializeSort };

// NOTE: this store backs the Inbound Referrals inbox (InboundReferralsListPage) only.
// stores/referralsStore.ts is a separate, legacy Review-typed referrals page — not this one.

// In display order. Awaiting Admission (OLIO-3176) sits between the two: referrals whose Selected
// outcome queued a patient who has not yet been admitted — out of the working list, not yet a
// record. The api decides membership (awaiting_admission); Active and History subtract it.
export type Tab = 'active' | 'awaitingAdmission' | 'history';

// The chip row's vocabulary (Active tab only). Each name is the Current Status pill it selects:
// 'actionNeeded' is the Flagged pill within the per-building queue, 'readyForReview' the Ready for
// Review pill — see InboundReferralsListPage's chipParams for the params behind each. The Status
// dropdown that used to sit beside the chips is gone (OLIO-3172): two controls under one label
// kept returning rows whose pills didn't match it.
export type StatusChip = 'all' | 'actionNeeded' | 'readyForReview';

// The History tab's chip row (OLIO-3295): the recorded outcome, filtered server-side on
// `outcome`. 'all' is every concluded referral the other filters allow, archived included only
// when the checkbox says so.
export type HistoryChip = 'all' | 'selected' | 'notSelected' | 'denied';

// Where the Filters slideout keeps its selection, mirroring admissionsStore's `filters` shape. The
// Status dropdown that used to live here went away with OLIO-3172; this holds the Referral Source
// selection (OLIO-3161), the receiving-SNF selection (OLIO-3471), and the History tab's "Show
// Archived Referrals" checkbox (OLIO-3295).
//
// `referralSource` holds the client's own reporting names, not the wording a referral captured:
// several wordings resolve to one name, so filtering on the raw value would split one hospital
// across its spellings. `payer` holds the resolved payer reporting names the same way (OLIO-3339).
// `snf` holds group ids (not names): the SNF filter is keyed on workflow_groups.group_id, the same
// value the Insights facility filter sends. The bar quick filter and the slideout copy both read and
// write this one key, so a pick in either surfaces in the other.
//
// `snf` is ONE selection shared across all three tabs (OLIO-3471): the buildings picked on Active
// stay picked when switching to Awaiting Admission. Only the label and the query param vary by tab —
// on Active/History it means the RECEIVING facility (sent as `group_id`), on Awaiting Admission it
// means the SELECTED (winning) facility (sent as `selected_facility`). The page owns that mapping.
export type InboundReferralsFilterState = {
  referralSource?: string[];
  payer?: string[];
  snf?: string[];
  showArchived?: boolean;
};

// Each tab has its own natural order: Active is newest-created first (the inbox), Awaiting
// Admission is most-recently-selected first (Time Since Selection, OLIO-3176), History is
// most-recently-concluded first (the mockup's Date Completed sort). Switching tabs resets to the
// tab's default — a Date Completed sort has no column to land on back on Active anyway.
export const ACTIVE_DEFAULT_SORT: SortingState = [{ id: 'createdAt', desc: true }];
export const AWAITING_DEFAULT_SORT: SortingState = [{ id: 'selectedAt', desc: true }];
export const HISTORY_DEFAULT_SORT: SortingState = [{ id: 'concludedAt', desc: true }];
// A record rather than a ternary so a fourth tab cannot silently inherit another tab's order.
const DEFAULT_SORTS: Record<Tab, SortingState> = {
  active: ACTIVE_DEFAULT_SORT,
  awaitingAdmission: AWAITING_DEFAULT_SORT,
  history: HISTORY_DEFAULT_SORT,
};
export const defaultSortFor = (tab: Tab): SortingState => DEFAULT_SORTS[tab];

// A History range is whole local days: the picker chooses calendar days in the viewer's zone, so
// the bounds sent are that zone's first and last instant of each day — the api honours instants
// as given (Workflow::QueryAttributes::DateRange) rather than re-bucketing them in UTC.
export const wholeDays = ([start, end]: [Date, Date]): [Date, Date] => [startOfDay(start), endOfDay(end)];

// The History date range's default — the last 30 days, like the outbound Active Referrals page
// (ReferralsFilterBar). A function, not a value: the store holds `null` until the person picks a
// range, and the page computes the default when History is shown, so a tab left open overnight
// does not keep yesterday's window.
export const defaultDateRange = (): [Date, Date] => {
  const now = new Date();
  return wholeDays([sub(now, { days: 30 }), now]);
};

// The list's page size, fixed — there is no page-size control on this list. Here rather than
// leaning on usePagination's default because the page itself now lives in this store.
export const PAGE_SIZE = 10;

type InboundReferralsActions = {
  setPage: (page: number) => void;
  setSort: (sort: SortingState) => void;
  setSearch: (search: string) => void;
  setTab: (tab: Tab) => void;
  setStatusChip: (chip: StatusChip) => void;
  setHistoryChip: (chip: HistoryChip) => void;
  setDateRange: (range: [Date, Date]) => void;
  setFilters: (
    filters: InboundReferralsFilterState | ((prev: InboundReferralsFilterState) => InboundReferralsFilterState)
  ) => void;
  setExpanded: (expanded: boolean) => void;
};

export type InboundReferralsState = {
  // The page being viewed (1-based). Here rather than in the list page's own useState because
  // opening a referral unmounts the list, and local state would put the person back on page 1
  // when they came back — the same reason the tab, chips, filters and sort live here.
  page: number;
  sort: SortingState;
  search: string;
  tab: Tab;
  statusChip: StatusChip;
  historyChip: HistoryChip;
  // The History tab's Date Completed range (OLIO-3295); null means "the default, computed when
  // shown" (defaultDateRange). The Active tab ignores it.
  dateRange: [Date, Date] | null;
  filters: InboundReferralsFilterState;
  // Whether the filter chip row is showing every chip or just the first line of them — read by
  // ChipBag, which owns the Show More / Show Less link (StateWithExpanded).
  expanded: boolean;
  actions: InboundReferralsActions;
};

const createInboundReferralsStore = (): [
  useStore: UseBoundStore<StoreApi<InboundReferralsState>>,
  useActions: () => InboundReferralsActions,
] => {
  const useStore = create<InboundReferralsState>((set) => ({
    page: 1,
    sort: ACTIVE_DEFAULT_SORT,
    search: '',
    tab: 'active',
    statusChip: 'all',
    historyChip: 'all',
    dateRange: null,
    filters: {},
    expanded: false,
    // Anything that changes which referrals come back sends the person to page 1 with it —
    // narrowing the list from page 3 would otherwise land them on a page the new result set may
    // not reach. It happens here rather than in an effect on the page because such an effect also
    // runs on mount, which would throw away the page being restored on the way back from a
    // referral. Paging and sorting are the exceptions: sorting resets through setPage, from
    // ControlledTable's onSortChange.
    actions: {
      setPage: (page) => set({ page }),
      setSort: (sort) => set({ sort }),
      setSearch: (search) => set({ search, page: 1 }),
      // Re-clicking the tab already showing changes nothing — the sort and the page a person
      // chose both survive.
      setTab: (tab) => set((state) => (state.tab === tab ? {} : { tab, sort: defaultSortFor(tab), page: 1 })),
      setStatusChip: (statusChip) => set({ statusChip, page: 1 }),
      setHistoryChip: (historyChip) => set({ historyChip, page: 1 }),
      setDateRange: (dateRange) => set({ dateRange: wholeDays(dateRange), page: 1 }),
      setFilters: (filters) =>
        set((state) => ({
          filters: typeof filters === 'function' ? filters(state.filters) : filters,
          page: 1,
        })),
      setExpanded: (expanded) => set({ expanded }),
    },
  }));

  const useActions = () => useStore((state) => state.actions);

  return [useStore, useActions];
};

export const [useInboundReferralsStore, useInboundReferralsActions] = createInboundReferralsStore();
